import { Button, Modal } from '@mui/material';
import { Col, Row } from "antd";
import React, { useEffect, useState, useMemo, useRef } from "react";
import styled from "styled-components";
import { getRandomImage } from "utils/config/reference";
import CloseIconss from "../../../utils/assets/images/closes.svg";
import SentryWrap from 'components/Sentry';

export default function ResultScreen({
  currentData,
  isOpenResult = false,
  setIsOpenResult,
  handleActiveIndex,
  isMuted = false,
}) {
  const [isCorrect, setIsCorrect] = useState(true);
  const [image, setImage] = useState("");
  const audioRef = useRef(null);
  const safePronouns = useMemo(() => {
    if (!currentData?.pronouns) return [];
    if (Array.isArray(currentData.pronouns)) return currentData.pronouns;
    return [];
  }, [currentData?.pronouns]);

  useEffect(() => {
    setImage(getRandomImage());
  }, [isOpenResult]);

  const handleContinue = () => {
    setIsOpenResult(false);
    handleActiveIndex();
  };

  const replaceRedWithDarkBlue = (htmlContent) => {
    if (!htmlContent) return '';

    const redColorRegex =
      /color\s*:\s*(rgb\(255,\s*0,\s*0\)|rgba\(255,\s*0,\s*0,\s*1\)|#ff0000|#f00\b|red\b)/i;

    return htmlContent.replace(/style\s*=\s*"([^"]*)"/gi, (_, style) => {
      if (!redColorRegex.test(style)) return `style="${style.trim()}"`;

      let updatedStyle = style.replace(redColorRegex, 'color: rgb(255, 0, 0)');

      if (!/font-weight\s*:\s*bold/i.test(updatedStyle)) {
        updatedStyle += '; font-weight: bold';
      }

      return `style="${updatedStyle.trim()}"`;
    });
  };


  useEffect(() => {
    if (isOpenResult && currentData?.answer_audio && !isMuted) {
      // Create a new Audio object
      audioRef.current = new Audio(currentData.answer_audio);
      audioRef.current.play().catch((err) => console.error("Audio play error:", err));
    } else {
      // Pause and reset the audio when modal closes
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, [isOpenResult, currentData?.answer_audio, isMuted]);

  return (
    <SentryWrap>
      <Modal
        open={isOpenResult}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            setIsOpenResult(false);
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ModalContent bgColor={"rgb(255, 255, 255)"}>
          <Row
            style={{
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <Explain>Explanation:</Explain>
          </Row>
          <div style={{ padding: "20px", paddingTop: 0 }}>
            <Row className='resultMdalRow' style={{ position: "relative" }}>
              <StyledButton isCorrect={!isCorrect}>
                <span style={{maxWidth: "72%"}}>
                {currentData?.type === "Fill_IN_THE_Blank"
                  ? currentData?.highlightWords
                      ?.map((highlight) => highlight?.text)
                      .join(", ")
                  : currentData?.correct_answer}
                  {currentData?.correct_answer_eng?`(${currentData?.correct_answer_eng})`:""}
                  </span>

                <div className='studntClosActvty'>  
                 <img src={CloseIconss} alt="Student activity" title="Student activity"  />
                </div>
              </StyledButton>
            </Row>
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TextMessageContainer>
                  {currentData?.wrongAnswerFeedBack}
                </TextMessageContainer>
              </div>
              <Row className='resultAIText'>
                <Col xs={12} lg={12} md={12} style={{ paddingLeft: 15 }}>
                  <div className='resultAITextLeft'> 
                    {currentData?.pronounsDetailOption && (
                      <IrregularText style={{ textAlign: "left", color:'black' }}>
                        Pronouns Detail
                      </IrregularText>
                    )}

                    {currentData?.pronounsDetailOption && (
                      <div
                        className="text-black"
                        dangerouslySetInnerHTML={{
                          __html: replaceRedWithDarkBlue(currentData?.pronounsDetailOption),
                        }}
                      />
                    )}

                    {safePronouns.length > 0 && (
                      <IrregularText>Pronouns</IrregularText>
                    )}

                    {safePronouns.map((pronoun, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "23px",
                        }}
                      >
                        <TextMessageContainer>
                          {pronoun?.pronounsWord || pronoun}
                        </TextMessageContainer>
                        <TextMessageContainer
                          style={{
                            color: pronoun?.isRegular ? "#f00" : "#FFFFFF",
                          }}
                        >
                          {pronoun?.word || ""}
                        </TextMessageContainer>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col xs={12} lg={12} md={12}>
                  <TextMessage style={{ marginTop: 20, color:'black' }}>
                    Try again,
                  </TextMessage>
                  <TextMessage style={{color:'black'}} >you got this!</TextMessage>
                  <div
                    className='roboAiImg'
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <RandomImage src={image} alt="Random" />
                  </div>
                </Col>
              </Row>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <ContinueButton onClick={handleContinue}>Continue</ContinueButton>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </SentryWrap>
  );
}

const IrregularText = styled.div`
  text-align: center;
  margin-inline: auto;
  margin-top: 10px;
  color: #fff;
`;

const ModalContent = styled.div`
  left: 50%;
  width: 50%;
  position: absolute;
  min-height: 440px;
  top: 50%;
  border-radius: 25px;
  transform: translate(-50%, -50%);
  background-color: ${(p) => p.bgColor || "#fff"};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media only screen and (max-width: 350px) {
    overflow: auto;
    height: 100%;
    max-height: 500px;
  }
    
  @media only screen and (max-width: 600px) {
    min-width: 90%;
  }
  @media only screen and (min-width: 601px) and (max-width: 768px) {
    width: 70%;
  }
`;

const Explain = styled.span`
  font-size: 15px;
  color: black;
  font: semi-bold;
  text-shadow: 2px 2px 5px rgba(0, 0, 0, 0.5);
  text-align:center;
`;
const TextMessage = styled.p`
  font-size: 16px;
  color: white;
  text-align: center;
  color: white;
  margin-bottom: 0;
`;
const TextMessageContainer = styled.p`
  font-size: 16px;
  text-align: center;
  color: black;
  margin-bottom: 0;
  max-width: 80%;
  @media Screen and (max-width: 768px) {
    max-width: 95%;
  }
`;

const StyledButton = styled(Button)`
  background-color: ${(p) =>
    p.isCorrect ? "rgb(180, 255, 219) !important" : "#f9b8d7 !important"};
  padding: 10px 24px;
  font-size: 16px;
  border-radius: 20px;
  color: black;
  text-transform: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: none;
  width: 100%;
  min-height: 45px;
  font-weight: bold !important;
  border-radius: 20px !important;
  margin-bottom: 20px;
  .MuiButton-label{
    max-width:80% !important;
  }
  &:hover {
    background-color: #a0e4a0;
  }
`;
const RandomImage = styled.img`
  height: 170px;
  margin-top: 20px;
  border-radius: 10px;
`;

const ContinueButton = styled.button`
  background-color: rgb(0, 15, 76);
  color: white;
  font-size: 16px;
  font-weight: bold;
  padding: 6px 14px;
  border-radius: 25px;
  border: none;
  cursor: pointer;
  text-align: center;
  outline: none;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: #34495e;
    transform: scale(1.05);
  }

  &:focus {
    outline: none;
  }
`;
